import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn"; // 시스템 공통 유틸
import { Page, Group, Layout, Button, Grid, ExcelUpload, FormControl, Table } from "@/comn/components";
import {
    useForm,
    useToast,
    useFetch,
    useModal,
    useAuth,
    useStore,
    useGrid,
    usePopup,
    useResource,
    useExcel,
    TGridSchema,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_LPCO_INFO_U,
    SG_COND_PER_DOC_LIST,
    SG_COND_PER_WG_FOB_LIST,
    SG_COND_RNGE_WG_FOB_LIST,
    SG_COND_PER_CNTR_LIST,
    SG_COND_PCT_FOB_LIST,
    SG_COND_MAX_FOB_LIST,
    SG_GFS_CD_LST,
} from "./services/OsciCfgnFeeService";
import { CommonErrors } from "@/comn/components";

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * Register LPCO Component
 * !== LPCO Component 등록 ==!
 */
export const OSCI0203021S = () => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const { appFmId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { openPopup } = usePopup();
    const [lpcoFeeList, setLpcoFeeList] = useState(comnUtils.getGridData([]));
    // GFS Code List !== GFS 코드 목록 ==!
    const [gfsCdList, setGfsCdList] = useState(comnUtils.getGridData([]));

    const [feeImpsCtraCd, setFeeImpsCtraCd] = useState("");
    const [feeImpsCndtCd, setFeeImpsCndtCd] = useState("");

    const refSgFeeList = useRef<TGridSchema | null>();

    const [stateLangCd, setStateLangCd] = useState(localStorage.getItem("lang")?.toUpperCase() || "EN");

    const { getResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0060" },
            { area: "comnCd", comnCd: "OSC0135" },
            { area: "comnCd", comnCd: "OSC0136" },
            { area: "comnCd", comnCd: "OSC0137" },
            { area: "comnCd", comnCd: "OSC0138" },
            { area: "comnCd", comnCd: "OSC0123" },
            { area: "comnCd", comnCd: "OSC0113" },
            { area: "comnCd", comnCd: "OSC0143" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Register LPCO Component !== LPCO Component 등록 ==!
        lpcoFeeRgsr: useForm({
            defaultSchema: SF_LPCO_INFO_U,
            defaultValues: { vldStrtDt: comnUtils.getDate(), orgCd: auth.get("orgCd"), orgNm: auth.get("orgNm") },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     *
     */
    const grid = {
        // Component List !== Component 목록 ==!
        feeCondPerDocList: useGrid({
            defaultSchema: SG_COND_PER_DOC_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // GFS Code List !== GFS 코드 목록 ==!
        gfsCdList: useGrid({
            defaultSchema: SG_GFS_CD_LST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_feeCondList: {
            head: {},
            cell: {},
            edit: {},
        },
        grid_gfsCdList: {
            head: {},
            cell: {},
            edit: {},
        },
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        /* Register LPCO Component !== LPCO Component 등록 ==! */
        createFeeInfo: useFetch({
            api: (data) => APIS.createFeeInfo({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onConfirm: () => {
                        navigate(URLS.osci0203024s + "/" + data.feeInfo?.content.feeImpsCtraId);
                    },
                    onCancel: () => {
                        navigate(URLS.osci0203024s + "/" + data.feeInfo?.content.feeImpsCtraId);
                    },
                });
            },
            onError: (error) => {
                modal.openModal({ content: error.response.data.errorMessage });
            },
        }),
        getOrgLpcoByPrcsList: useFetch({
            api: (data) => {
                return lpcoUtils.getOrgLpcoByPrcsList(data);
            },
            onSuccess: (data) => {
                console.log("getOrgLpcoByPrcsList onSuccess ", data);
                setOrgLpcoList(data || []);
            },
            onError: () => {},
            showToast: false,
        }),
    };
    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : 재포장 품목 신청서 그리드 클릭
     *      ...
     * }
     */
    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */

    // ORG LIST
    const [orgCdList, setOrgCdList] = useState([]);
    // ORG LPCO LIST
    const [orgLpcoList, setOrgLpcoList] = useState([]);

    const handler = {
        // Save Component !== LPCO Component 저장 ==!
        createFeeInfo: form.lpcoFeeRgsr.handleSubmit(async () => {
            // check duplicate
            let lpcoFeeList = grid.feeCondPerDocList.getData() || [];

            if (feeImpsCtraCd == "W" || feeImpsCtraCd == "F" || feeImpsCtraCd == "I" || feeImpsCtraCd == "C") {
                // grid data one more
                if (lpcoFeeList.length == 0) {
                    // You can enter one or more DATA and save it.
                    modal.openModal({
                        content: "msg.osc.00037",
                    });
                    return false;
                }

                // grid validate
                let errResult = grid.feeCondPerDocList.validate() || {};
                errResult["errors"] = errResult?.errors || [];

                /*
Total Weight(KG) Base
- Per(PE) : Only one row possible !== 한 행만 가능 ==!

FOB Base
- Per(PE) : Only one row possible !== 한 행만 가능 ==!
- Percent(%)(PC) : Only one row allowed, cannot exceed 100 !== 한 행만 가능, 100 초과 불가능 ==!
- MAXIMUM : AMOUNT/PERCENT TYPE Duplicate is not possible. Up to 2 rows are allowed. !== AMOUNT/PERCENT TYPE 중복 불가능 2행까지 가능. ==!
            If percent(PE), value cannot exceed 100 !== percent(PE) 이면 value 100초과 불가능 ==!

Container Base
- Per(PE) : Container size cannot be duplicated, up to 2 rows possible. !== 컨테이너 사이즈 중복 불가능 2행까지 가능. ==!
*/

                if (feeImpsCtraCd == "W" && feeImpsCndtCd == "PE") {
                    if (lpcoFeeList.length > 1) {
                        errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00078", ""));
                        // Only one row can be saved.
                        // modal.openModal({
                        //     content: "msg.osc.00078",
                        // });
                        //return false;
                    }
                } else if (["F", "I"].indexOf(feeImpsCtraCd) > -1) {
                    // FOB
                    // percent
                    if (feeImpsCndtCd == "PE" || feeImpsCndtCd == "PC") {
                        if (lpcoFeeList.length > 1) {
                            errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00078", ""));
                        } else {
                            // if (lpcoFeeList[0]["feeDtlCtraValue"] > 100) {
                            //     errResult.errors.push(
                            //         lpcoUtils.getErrorObj(
                            //             1,
                            //             "L_PCT",
                            //             "msg.osc.00079",
                            //             lpcoFeeList[0]["feeDtlCtraValue"],
                            //         ),
                            //     );
                            // }
                        }
                    }
                    // maximum
                    if (feeImpsCndtCd == "MX") {
                        if (lpcoFeeList.length > 2) {
                            errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00080", ""));
                        }
                        if (lpcoFeeList.length > 0) {
                            const duplMap: { [key: string]: boolean } = {};
                            for (let i = 0; i < lpcoFeeList.length; i++) {
                                const feeDtlCtraTpCd = lpcoFeeList[i]["feeDtlCtraTpCd"];
                                if (feeDtlCtraTpCd && duplMap[feeDtlCtraTpCd]) {
                                    errResult.errors.push(
                                        lpcoUtils.getErrorObj(
                                            i + 1,
                                            "L_AMT_PCT_TP",
                                            "msg.osc.00017",
                                            await lpcoUtils.getCodeLabel(
                                                getResource("comnCd", "OSC0137"),
                                                feeDtlCtraTpCd,
                                            ),
                                        ),
                                    );
                                } else {
                                    duplMap[feeDtlCtraTpCd] = true;
                                }

                                if (feeDtlCtraTpCd == "PE" && lpcoFeeList[i]["feeDtlCtraValue"] > 100) {
                                    errResult.errors.push(
                                        lpcoUtils.getErrorObj(
                                            i + 1,
                                            "L_VAL",
                                            "msg.osc.00079",
                                            lpcoFeeList[i]["feeDtlCtraValue"],
                                        ),
                                    );
                                }
                            }
                        }
                    }
                } else if (feeImpsCtraCd == "C") {
                    // container size
                    if (lpcoFeeList.length > 2) {
                        errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00080", ""));
                    }

                    const duplMap: { [key: string]: boolean } = {};
                    for (let i = 0; i < lpcoFeeList.length; i++) {
                        const feeCntrSizeCd = lpcoFeeList[i]["feeCntrSizeCd"];
                        if (feeCntrSizeCd && duplMap[feeCntrSizeCd]) {
                            errResult.errors.push(
                                lpcoUtils.getErrorObj(
                                    i + 1,
                                    "L_CNTR_SIZE",
                                    "msg.osc.00017",
                                    await lpcoUtils.getCodeLabel(getResource("comnCd", "OSC0138"), feeCntrSizeCd),
                                ),
                            );
                        } else {
                            duplMap[feeCntrSizeCd] = true;
                        }
                    }
                }

                if (feeImpsCndtCd == "RA") {
                    // RANGE
                    if (lpcoFeeList.length > 0) {
                        for (let i = 0; i < lpcoFeeList.length; i++) {
                            const rowData = lpcoFeeList[i];

                            console.log(" rowData ", rowData);

                            if (rowData["rngeMinValue"] > rowData["rngeMaxValue"]) {
                                // The range value is invalid. !== 범위값이 유효하지 않습니다.==!
                                errResult.errors.push(
                                    lpcoUtils.getErrorObj(i + 1, "L_MAX_VAL", "msg.osc.00088", rowData["rngeMaxValue"]),
                                );
                            }

                            if (rowData["feeDtlAmt"] != undefined && rowData["feeDtlAmt"] <= 0) {
                                // The amount must be greater than 0. !== 금액은 0보다 커야합니다. ==!
                                errResult.errors.push(
                                    lpcoUtils.getErrorObj(i + 1, "L_AMT", "msg.osc.00089", rowData["feeDtlAmt"]),
                                );
                            }
                            if (i > 0) {
                                if (rowData["rngeMinValue"] != lpcoFeeList[i - 1]["rngeMaxValue"]) {
                                    // The range value is invalid. !== 범위값이 유효하지 않습니다.==!
                                    errResult.errors.push(
                                        lpcoUtils.getErrorObj(
                                            i + 1,
                                            "L_MIN_VAL",
                                            "msg.osc.00088",
                                            rowData["rngeMinValue"],
                                        ),
                                    );
                                }
                            }
                        }
                    }
                }

                if (errResult && errResult.errors?.length > 0) {
                    // sort by row
                    errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                    // error layer popup
                    modal.openModal({
                        content: <CommonErrors {...errResult} />,
                        size: "lg",
                        draggable: true,
                    });
                    return false;
                } else {
                }
            } else {
                // grid no data
                lpcoFeeList = [];
            }

            console.log("lpcoFeeList ", lpcoFeeList);

            // GFS Code List validate & collect !== GFS 코드 목록 검증 및 수집 ==!
            let gfsSaveList: any[] = [];
            gfsSaveList = grid.gfsCdList.getViewData() || [];

            let gfsErrResult = grid.gfsCdList.validate() || {};
            gfsErrResult["errors"] = gfsErrResult?.errors || [];

            // GFS 코드 중복 체크 !== check duplicate GFS code ==!
            const gfsDuplMap: { [key: string]: boolean } = {};
            for (let i = 0; i < gfsSaveList.length; i++) {
                const gfsCd = gfsSaveList[i]["gfsCd"];
                if (gfsCd && gfsDuplMap[gfsCd]) {
                    gfsErrResult.errors.push(lpcoUtils.getErrorObj(i + 1, "L_GFS_CD", "msg.osc.00017", gfsCd));
                } else {
                    gfsDuplMap[gfsCd] = true;
                }
            }

            if (gfsErrResult && gfsErrResult.errors?.length > 0) {
                gfsErrResult.errors = lpcoUtils.errorListSortByRow(gfsErrResult.errors);
                modal.openModal({
                    content: <CommonErrors {...gfsErrResult} />,
                    size: "lg",
                    draggable: true,
                });
                return false;
            }

            const data = { ...form.lpcoFeeRgsr.getValues(), feeList: lpcoFeeList, gfsList: gfsSaveList };
            console.log("data ", data);
            modal.openModal({
                content: "msg.osc.00090",
                onConfirm: () => {
                    fetch.createFeeInfo.fetch(data);
                },
            });
        }),
        click_Btn_AddGridList: async () => {
            console.log("feeImpsCtraCd ", feeImpsCtraCd);
            console.log("feeImpsCndtCd ", feeImpsCndtCd);
            // GRID DATA
            const lpcoFeeList = grid.feeCondPerDocList.getData() || [];
            // grid validate
            let errResult = (await grid.feeCondPerDocList.validate()) || [];
            errResult["errors"] = errResult?.errors || [];

            console.log("errResult ", errResult);

            // !== PER, PERCENT는 한행만 ==!
            if (feeImpsCtraCd != "C" && (feeImpsCndtCd == "PE" || feeImpsCndtCd == "PC")) {
                if ((grid.feeCondPerDocList.getData() || []).length == 1) {
                    errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00078", ""));
                }
            }
            // !== 범위 RANGE면 최소값 최대값 체크 ==!
            if (feeImpsCndtCd == "RA") {
                if (lpcoFeeList.length > 0) {
                    for (let i = 0; i < lpcoFeeList.length; i++) {
                        const rowData = lpcoFeeList[i];

                        if (rowData["rngeMinValue"] > rowData["rngeMaxValue"]) {
                            // The range value is invalid. !== 범위값이 유효하지 않습니다.==!
                            errResult.errors.push(
                                lpcoUtils.getErrorObj(i + 1, "L_MAX_VAL", "msg.osc.00088", rowData["rngeMaxValue"]),
                            );
                        }

                        if (rowData["feeDtlAmt"] != undefined && rowData["feeDtlAmt"] <= 0) {
                            // The amount must be greater than 0. !== 금액은 0보다 커야합니다. ==!
                            errResult.errors.push(
                                lpcoUtils.getErrorObj(i + 1, "L_AMT", "msg.osc.00089", rowData["feeDtlAmt"]),
                            );
                        }

                        if (i > 0) {
                            if (rowData["rngeMinValue"] != lpcoFeeList[i - 1]["rngeMaxValue"]) {
                                // The range value is invalid. !== 범위값이 유효하지 않습니다.==!
                                errResult.errors.push(
                                    lpcoUtils.getErrorObj(i + 1, "L_MIN_VAL", "msg.osc.00088", rowData["rngeMinValue"]),
                                );
                            }
                        }
                    }
                }
            }

            if (feeImpsCndtCd == "MX" || (feeImpsCtraCd == "C" && feeImpsCndtCd == "PE")) {
                if (lpcoFeeList.length == 2) {
                    errResult.errors.push(lpcoUtils.getErrorObj("", "", "msg.osc.00080", ""));
                }
            }
            if (errResult && errResult.errors?.length > 0) {
                // sort by row
                errResult.errors = lpcoUtils.errorListSortByRow(errResult.errors);
                // error layer popup
                modal.openModal({
                    content: <CommonErrors {...errResult} />,
                    size: "lg",
                    draggable: true,
                });
                return false;
            }

            let rngeMinValue;
            let rngeTxt;
            if (feeImpsCndtCd == "RA") {
                // !== 마지막행 최대값을 다음 행 최소값으로 입력 ==!
                if (lpcoFeeList.length == 0) {
                    rngeMinValue = 0;
                } else {
                    rngeMinValue = lpcoFeeList[lpcoFeeList.length - 1]["rngeMaxValue"];
                }
                if (feeImpsCtraCd == "I") {
                    rngeTxt = "<= CIF <";
                } else if (feeImpsCtraCd == "F") {
                    rngeTxt = "<= FOB <";
                } else if (feeImpsCtraCd == "W") {
                    rngeTxt = "<= Weight(KG) <";
                }
            }

            grid.feeCondPerDocList.addRow({ rngeTxt: rngeTxt, rngeMinValue: rngeMinValue });
        },
        click_Btn_DeleteGridList: () => {
            const seltList: any[] = grid.feeCondPerDocList.getChecked() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }

            grid.feeCondPerDocList.deleteRow("checkbox");

            // modal.openModal({
            //     content: "msg.osc.00095",
            //     onConfirm: () => {
            //         grid.lpcoAtchFileList.deleteRow("checkbox");
            //     },
            // });
        }, // GFS 코드 목록 : 행 추가 !== add GFS row ==!
        click_Btn_GfsAddGridList: () => {
            grid.gfsCdList.addRow();
        },
        // GFS 코드 목록 : 선택 행 삭제 !== delete selected GFS row ==!
        click_Btn_GfsDeleteGridList: () => {
            const seltList: any[] = grid.gfsCdList.getSelectedRow() || [];
            if (comnUtils.isEmpty(seltList)) {
                modal.openModal({ content: "msg.osc.00009" });
                return;
            }
            grid.gfsCdList.deleteRow("radio");
        },
        setInit: async () => {
            // 기관 LPCO SELECT 목록
            fetch.getOrgLpcoByPrcsList.fetch(auth.get("orgCd"));
        },
    };

    // useEffect
    useEffect(() => {
        handler.setInit();
    }, []);

    useEffect(() => {
        console.log("useEffect [feeImpsCtraCd, feeImpsCndtCd]");
        if (feeImpsCtraCd == "D" && feeImpsCndtCd == "PE") {
            // doc base + per
            refSgFeeList.current = SG_COND_PER_DOC_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F" || feeImpsCtraCd == "W") && feeImpsCndtCd == "PE") {
            // fob, cif, weight + per
            refSgFeeList.current = SG_COND_PER_WG_FOB_LIST;
        } else if ((feeImpsCtraCd == "I" || feeImpsCtraCd == "F" || feeImpsCtraCd == "W") && feeImpsCndtCd == "RA") {
            // fob, cif, weight + range
            refSgFeeList.current = SG_COND_RNGE_WG_FOB_LIST;
        } else if (feeImpsCtraCd == "C" && feeImpsCndtCd == "PE") {
            // container + per
            refSgFeeList.current = SG_COND_PER_CNTR_LIST;
        } else if ((feeImpsCtraCd == "F" || feeImpsCtraCd == "I") && feeImpsCndtCd == "PC") {
            // fob, cif + percent
            refSgFeeList.current = SG_COND_PCT_FOB_LIST;
        } else if ((feeImpsCtraCd == "F" || feeImpsCtraCd == "I") && feeImpsCndtCd == "MX") {
            // fob, cif + maximum
            refSgFeeList.current = SG_COND_MAX_FOB_LIST;
        } else {
            // others
            refSgFeeList.current = SG_COND_PER_DOC_LIST;
        }

        grid.feeCondPerDocList.setSchema(refSgFeeList.current);
        setLpcoFeeList(comnUtils.getGridData([])); // re-rendering
    }, [feeImpsCtraCd, feeImpsCndtCd]);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_FEE_CLCU_INFO"} titleSize={2}></Group.Title>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.lpcoFeeRgsr.schema.orgNm}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoFeeRgsr.schema.appCdNo}
                                options={orgLpcoList}
                                onChange={(ev: any) => {
                                    const result = orgLpcoList.find((obj) => obj["value"] == ev);
                                    let appTpCd = result ? result["appTpCd"] : "";
                                    form.lpcoFeeRgsr.setValue("appTpCd", appTpCd);
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoFeeRgsr.schema.appTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoFeeRgsr.schema.lpcoAplyTpCd}
                                onChange={(v: any) => {
                                    form.lpcoFeeRgsr.setValue("cmbiBllYn", v == "N" ? "Y" : "N");
                                }}
                            ></Group.Control>
                            <Group.Control {...form.lpcoFeeRgsr.schema.cmbiBllYn}></Group.Control>
                        </Group.Row>

                        <Group.Row>
                            <Group.Control {...form.lpcoFeeRgsr.schema.feeRuleNm}></Group.Control>
                            <Group.Control {...form.lpcoFeeRgsr.schema.feeImpsTpCd}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control
                                {...form.lpcoFeeRgsr.schema.feeImpsCtraCd}
                                onChange={(v) => {
                                    console.log("VV1", v);
                                    let arrExcludes: any[] = [];
                                    if (v == "D" || v == "C") {
                                        arrExcludes = "RA,PC,MX".split(",");
                                    } else if (v == "W") {
                                        arrExcludes = "MX,PC".split(",");
                                    } else if (v == "F" || v == "I") {
                                        arrExcludes = [];
                                    }
                                    setFeeImpsCtraCd(v);

                                    form.lpcoFeeRgsr.setValue("feeImpsCndtCd", "");

                                    form.lpcoFeeRgsr.setSchema("feeImpsCndtCd", {
                                        excludes: arrExcludes,
                                    });
                                    if (v == "M") {
                                        form.lpcoFeeRgsr.setValue("baseFee", "0");
                                        form.lpcoFeeRgsr.setSchema("feePrgmNm", { require: false });
                                        form.lpcoFeeRgsr.setSchema("baseFee", { readOnly: true });
                                    } else if (v == "N") {
                                        form.lpcoFeeRgsr.setValue("feeImpsCndtCd", "PE");
                                        form.lpcoFeeRgsr.setValue("baseFee", "0");
                                        form.lpcoFeeRgsr.setSchema("feeImpsCndtCd", { readOnly: true });
                                        form.lpcoFeeRgsr.setSchema("baseFee", { readOnly: true });
                                    } else {
                                        form.lpcoFeeRgsr.setSchema("feeImpsCndtCd", { readOnly: false });
                                        form.lpcoFeeRgsr.setSchema("baseFee", { readOnly: false });
                                    }
                                }}
                            ></Group.Control>
                            {feeImpsCtraCd != "U" && feeImpsCtraCd != "M" && (
                                <Group.Control
                                    {...form.lpcoFeeRgsr.schema.feeImpsCndtCd}
                                    onChange={(v) => {
                                        console.log("VV2", v);
                                        setFeeImpsCndtCd(v);
                                    }}
                                ></Group.Control>
                            )}{" "}
                            {(feeImpsCtraCd == "U" || feeImpsCtraCd == "M") && (
                                <Group.Control
                                    {...form.lpcoFeeRgsr.schema.feePrgmNm}
                                    required={feeImpsCtraCd != "M"}
                                ></Group.Control>
                            )}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoFeeRgsr.schema.srvcFeeCurrCd}></Group.Control>
                            <Group.Control {...form.lpcoFeeRgsr.schema.baseFee}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoFeeRgsr.schema.vldStrtDt}></Group.Control>
                            <Group.Control {...form.lpcoFeeRgsr.schema.vldEndDt}></Group.Control>
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.lpcoFeeRgsr.schema.feeDesc} controlSize={10}></Group.Control>
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            {feeImpsCtraCd != "U" && feeImpsCtraCd != "D" && feeImpsCtraCd != "M" && feeImpsCtraCd != "N" && (
                <Group>
                    <Group.Body>
                        <Group.Title title={"T_CNDT"} titleSize={2}></Group.Title>
                        <Layout direction="row">
                            <Layout.Left></Layout.Left>
                            <Layout.Right>
                                <Button
                                    role="gridAdd"
                                    onClick={() => {
                                        handler.click_Btn_AddGridList();
                                    }}
                                ></Button>
                                <Button
                                    role="gridDelete"
                                    onClick={() => {
                                        handler.click_Btn_DeleteGridList();
                                    }}
                                ></Button>
                            </Layout.Right>
                        </Layout>
                        {/*
                         * 그리드
                         * @ 그리드 스키마 주입 : {...grid.[그리드이름].grid}
                         * @ 데이터 data={fetch.[fetch 명].data?.[api 리턴 vo 명]}
                         * @ 셀클릭이벤트 연결 : onCellClick={handler.[그리드 이벤트 핸들러명]}
                         */}
                        <Grid
                            {...grid.feeCondPerDocList.grid}
                            data={lpcoFeeList} /* fetch.getRpckItmAppList.data?.lpcoFeeList */
                            render={render.grid_feeCondList}
                            // onCellClick={handler.click_Grid_RpckItmAppList}
                            // onRowClick={handler.click_GridRow_RpckItmAppList}
                        />
                    </Group.Body>
                </Group>
            )}

            {/* GFS Code List !== GFS 코드 목록 (등록) : 항상 표시 ==! */}
            <Group>
                <Group.Body>
                    <Group.Title title={"L_GFS_CD_LST"} titleSize={2}></Group.Title>
                    <Layout direction="row">
                        <Layout.Left></Layout.Left>
                        <Layout.Right>
                            <Button
                                icon="up"
                                onClick={() => {
                                    // 선택된 그리드 Row를 위쪽으로 이동
                                    grid.gfsCdList.upRow(grid.gfsCdList.getSelectedRow());
                                }}
                            ></Button>
                            <Button
                                icon="down"
                                onClick={() => {
                                    // 선택된 그리드 Row를 아래쪽으로 이동
                                    grid.gfsCdList.downRow(grid.gfsCdList.getSelectedRow());
                                }}
                            ></Button>
                            <Button
                                role="gridAdd"
                                onClick={() => {
                                    handler.click_Btn_GfsAddGridList();
                                }}
                            ></Button>
                            <Button
                                role="gridDelete"
                                onClick={() => {
                                    handler.click_Btn_GfsDeleteGridList();
                                }}
                            ></Button>
                        </Layout.Right>
                    </Layout>
                    <Grid {...grid.gfsCdList.grid} data={gfsCdList} render={render.grid_gfsCdList} />
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.osci0203022q);
                        }}
                    ></Button>
                    <Button
                        role="reset"
                        onClick={() => {
                            form.lpcoFeeRgsr.reset({ ...lpcoUtils.orgInfo });

                            grid.feeCondPerDocList.resetData();
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button role="save" onClick={handler.createFeeInfo}></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
